import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    CreateInsurerDocument,
    type CreateInsurerMutation,
    type CreateInsurerMutationVariables,
} from '../../../api/mutations/createInsurer';
import { InsurerIntegrationProvider, type InsurerIntegrationSettings } from '../../../api/types';
import useLoadingButton from '../../../components/button/useLoadingButton';
import Form from '../../../components/fields/Form';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../utilities/useHandleError';
import { useInsurerFormValidator, type NewInsurerFormValues } from '../InsurerDetailsPage/InsurerForm';
import CreateInsurerForm, { defaultValues } from '../InsurerDetailsPage/InsurerForm/CreateInsurerFrom';

export const getInsurerIntegrationSettings = ({
    integrationProvider,
    integrationSettings,
}: {
    integrationProvider: InsurerIntegrationProvider;
    integrationSettings: InsurerIntegrationSettings;
}) => {
    switch (integrationProvider) {
        case InsurerIntegrationProvider.Email:
            return pick('email', integrationSettings);

        case InsurerIntegrationProvider.Eazy:
            return pick('eazy', integrationSettings);

        default:
            return null;
    }
};

const AddInsurerPage = () => {
    const { t } = useTranslation(['insurerDetails']);
    const navigate = useNavigate();
    const { setLoading, LoadingButton } = useLoadingButton();
    const validate = useInsurerFormValidator();

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError<NewInsurerFormValues>(
        async values => {
            const { integrationProvider, integrationSettings, ...settings } = values;

            // submitting message
            message.loading({
                content: t('insurerDetails:messages.createSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit creation
            await apolloClient.mutate<CreateInsurerMutation, CreateInsurerMutationVariables>({
                mutation: CreateInsurerDocument,
                variables: {
                    settings,
                    integrationSettings: getInsurerIntegrationSettings({ integrationProvider, integrationSettings }),
                },
            });

            // inform about success
            message.success({
                content: t('insurerDetails:messages.createSuccessful'),
                key: 'primary',
            });
            setLoading(false);
            // then go back to list
            navigate(`/admin/insurers`);
        },
        [apolloClient, navigate, setLoading, t]
    );

    return (
        <ConsolePageWithHeader
            footer={[
                <LoadingButton key="submit" form="insurerForm" htmlType="submit" type="primary">
                    {t('insurerDetails:actions.create')}
                </LoadingButton>,
            ]}
            onBack={() => navigate('/admin/insurers')}
            title={t('insurerDetails:addTitle')}
        >
            <Formik initialValues={defaultValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit, values }) => (
                    <Form id="insurerForm" name="insurerForm" onSubmitCapture={handleSubmit}>
                        <CreateInsurerForm />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default AddInsurerPage;
